import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { QueryClientProvider } from '@tanstack/react-query'
import { ConfigProvider, App as AntdApp, theme as antdTheme } from 'antd'
import zhCN from 'antd/locale/zh_CN'
import dayjs from 'dayjs'
import 'dayjs/locale/zh-cn'

import { routeTree } from './routeTree.gen'
import { queryClient } from './lib/queryClient'
import { initSentry } from './lib/sentry'
import { bootstrapAuth } from './auth/session'
import { useEffectiveTheme } from './lib/theme'
// 必须早于任何组件用 dayjs.tz —— 给 dayjs 装 utc + timezone 插件并 setDefault('Asia/Shanghai')
import './lib/time'
import './styles.css'

dayjs.locale('zh-cn')
initSentry()
await bootstrapAuth()

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

function AdminApp() {
  const effective = useEffectiveTheme()
  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        cssVar: {},
        hashed: false,
        algorithm:
          effective === 'dark'
            ? antdTheme.darkAlgorithm
            : antdTheme.defaultAlgorithm,
        token: {
          colorPrimary: '#1677ff',
          borderRadius: 6,
        },
        components: {
          Menu:
            effective === 'dark'
              ? {
                  darkItemBg: '#141414',
                  darkSubMenuItemBg: '#141414',
                  darkItemHoverBg: 'rgba(255, 255, 255, 0.06)',
                  darkItemSelectedBg: 'rgba(22, 119, 255, 0.22)',
                }
              : undefined,
        },
      }}
    >
      <AntdApp style={{ minHeight: '100vh' }}>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </AntdApp>
    </ConfigProvider>
  )
}

const rootEl = document.getElementById('root')
if (!rootEl) throw new Error('#root not found in index.html')

createRoot(rootEl).render(
  <StrictMode>
    <AdminApp />
  </StrictMode>,
)
